import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getFilteredPolls,
  postPollsVote
} from '../../common/redux/actions/PollsActions';

function PollModal(props, context) {
  const dispatch = useDispatch();
  const { profile } = props;
  const [show, setShow] = useState(true);
  const [showPollQuestions, setShowPollQuestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPollIndex, setCurrentPollIndex] = useState(0);
  const [filteredPollsToShow, setFilteredPollsToShow] = useState([]);
  const hasFetched = useRef({});

  const filteredPolls = useSelector((state) => state.PollsState.filtered.items);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    if (profile?.id && !hasFetched.current[profile.id]) {
      hasFetched.current[profile.id] = true;
      const filters = {
        country: profile?.country,
        gender: profile?.gender,
        nationality: profile?.nationality,
        age: profile?.age,
        profile_id: profile?.id
      };
      setLoading(true);
      dispatch(getFilteredPolls(filters, { signal: abortController.signal }))
        .finally(() => {
          if (isMounted.current) {
            setLoading(false);
          }
        })
        .catch((err) => {
          if (isMounted.current && err.name !== 'AbortError') {
            setError('Failed to load poll');
          }
        });
    }

    return () => {
      abortController.abort();
    };
  }, [profile, dispatch]);

  // Helper to get skipped poll ids from localStorage
  const getSkippedPollIds = () => {
    if (!profile?.id) return [];
    const key = `skipped_polls_${profile.id}`;
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  };

  // Helper to set skipped poll ids in localStorage
  const setSkippedPollIds = (pollIds) => {
    if (!profile?.id) return;
    const key = `skipped_polls_${profile.id}`;
    localStorage.setItem(key, JSON.stringify(pollIds));
  };

  // Filter polls based on skipped ids
  useEffect(() => {
    // Ensure filteredPolls is always an array
    const pollsArray = Array.isArray(filteredPolls) ? filteredPolls : [];
    if (!profile?.id) {
      setFilteredPollsToShow([]);
      return;
    }
    const skippedIds = getSkippedPollIds();
    const filtered = pollsArray.filter(
      (poll) => poll && !skippedIds.includes(poll.id)
    );
    setFilteredPollsToShow(filtered);
    setCurrentPollIndex(0);
  }, [filteredPolls, profile?.id]);

  // If no polls or loading, don't render
  if (loading || !filteredPollsToShow || filteredPollsToShow.length === 0) return null;

  const handleAcceptPollQuestions = () => {
    setShow(false);
    setShowPollQuestions(true);
    setCurrentPollIndex(0);
  };

  const handleRejectPollQuestions = () => {
    setShow(false);
    setShowPollQuestions(false);
    if (profile?.id && filteredPollsToShow.length > 0) {
      // Add all poll ids to skipped polls for this profile
      // const key = `skipped_polls_${profile.id}`;
      const existing = getSkippedPollIds();
      const allPollIds = filteredPollsToShow.map((poll) => poll.id);
      // Merge and deduplicate
      const newSkipped = Array.from(new Set([...existing, ...allPollIds]));
      setSkippedPollIds(newSkipped);
    }
  };

  const handleClickVoteButton = async(choiceId) => {
    try {
      // Use filteredPollsToShow instead of filteredPolls for voting
      await dispatch(
        postPollsVote(
          {
            question_id: filteredPollsToShow[currentPollIndex].id,
            choice_id: choiceId
          },
          true
        )
      );

      if (isMounted.current) {
        if (currentPollIndex === filteredPollsToShow.length - 1) {
          setShowPollQuestions(false);
          setShow(false);
        } else {
          setCurrentPollIndex((idx) => idx + 1);
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setError('Failed to submit vote');
      }
    }
  };

  const handleNext = () => {
    if (currentPollIndex < filteredPollsToShow.length - 1) {
      setCurrentPollIndex((idx) => idx + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPollIndex > 0) {
      setCurrentPollIndex((idx) => idx - 1);
    }
  };

  const handleSkip = () => {
    if (!profile?.id) return;
    const skippedIds = getSkippedPollIds();
    const pollId = filteredPollsToShow[currentPollIndex]?.id;
    if (pollId && !skippedIds.includes(pollId)) {
      const newSkipped = [...skippedIds, pollId];
      setSkippedPollIds(newSkipped);
      // Remove the skipped poll from the list and update index
      const newPolls = filteredPollsToShow.filter(
        (p, idx) => idx !== currentPollIndex
      );
      setFilteredPollsToShow(newPolls);
      if (currentPollIndex >= newPolls.length || newPolls.length === 0) {
        setShowPollQuestions(false);
        setShow(false);
      }
    }
  };

  // Initial modal: ask if user wants to answer
  return (
    <>
      <Modal show={show} centered onHide={() => setShow(false)}>
        <Modal.Header style={{ justifyContent: 'center' }}>
          <Modal.Title style={{ fontSize: '1rem' }}>
            <b>
              {context.t(
                'We have new poll questions. Would you like to answer?'
              )}
            </b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: 'flex', justifyContent: 'center' }}>
          <p style={{ margin: 0 }}>{filteredPollsToShow[0]?.description}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRejectPollQuestions}>
            {context.t("I don't want to answer")}
          </Button>
          <Button variant="success" onClick={handleAcceptPollQuestions}>
            {context.t('I want to answer')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showPollQuestions}
        centered
        onHide={() => setShowPollQuestions(false)}
      >
        <Modal.Header style={{ justifyContent: 'center' }}>
          <Modal.Title style={{ fontSize: '1rem' }}>
            <b>
              {context.t('Community poll')} ({currentPollIndex + 1} /
              {filteredPollsToShow.length})
            </b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: 'flex', justifyContent: 'center' }}>
          <p style={{ margin: 0 }}>
            {filteredPollsToShow[currentPollIndex]?.description}
          </p>
        </Modal.Body>
        <Modal.Footer style={{ flexDirection: 'column' }}>
          <div
            style={{
              display: 'grid',
              gap: '5px',
              width: '100%',
              marginBottom: '10px'
            }}
          >
            {filteredPollsToShow[currentPollIndex]?.choices?.map((choice) => (
              <Button
                variant="outline-secondary"
                key={choice.id}
                onClick={() => handleClickVoteButton(choice.id)}
              >
                {choice.description || choice.id}
              </Button>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            {currentPollIndex > 0 && (
              <Button
                variant="outline-primary"
                onClick={handlePrevious}
                disabled={currentPollIndex === 0}
              >
                {context.t('Previous')}
              </Button>
            )}
            {/* Show Skip button only if more than one poll exists */}
            {filteredPollsToShow.length > 1 && (
              <Button
                variant="outline-primary"
                onClick={handleSkip}
                style={currentPollIndex === 0 ? { marginLeft: 'auto' } : {}}
              >
                {context.t('Skip')}
              </Button>
            )}
            {filteredPollsToShow.length === 1
              && currentPollIndex < filteredPollsToShow.length - 1 && (
                <Button
                  variant="outline-primary"
                  onClick={handleNext}
                  disabled={currentPollIndex === filteredPollsToShow.length - 1}
                  style={currentPollIndex === 0 ? { marginLeft: 'auto' } : {}}
                >
                  {context.t('Next')}
                </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
      {error && <div style={{ color: 'red' }}>{error}</div>}
    </>
  );
}

PollModal.propTypes = {
  profile: PropTypes.object
};

PollModal.contextTypes = {
  t: PropTypes.func.isRequired
};

export default PollModal;
